import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
  fulfillmentGroupInsuranceEligible,
  getFulfillmentGroupParcelMaxInsuredValues,
  getInsuranceAmountIncludedByDefaultThreshold,
  getParcelInsuranceCost,
  NO_INSURANCE_REASON,
  PARCEL_THRESHOLD_REASON,
} from "@redotech/redo-fulfillment-operations-common/fulfillment-group-insurance";
import {
  type OutboundParcel,
  type ParcelInsuranceCoverageType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { Team } from "@redotech/redo-model/team";

export type InsuranceInfo =
  | { canEdit: false; reason: NO_INSURANCE_REASON }
  | {
      canEdit: true;
      isBulkEdit: boolean;
      mixedCustomInsurance: boolean;
      insuredAmount: number | null;
      totalFullCoverageInsuranceCost: number;
      customInsuranceDisplayedCost: number;
      coverageExceedsMaximum: boolean;
      insuranceIncludedThreshold: number;
      includedCoverageAmount: number;
      maxCoverageAmount: number;
      groupCanBeFullyInsured: boolean;
      insuranceCoverageReason: PARCEL_THRESHOLD_REASON;
      insuranceUiSelection: ParcelInsuranceCoverageType;
      coverageType: ParcelInsuranceCoverageType | null;
    };

export type EditableInsuranceInfo = Extract<InsuranceInfo, { canEdit: true }>;

function getParcelIndexForInsurance({
  group,
  parcel,
  parcelBeingEditedIndex,
}: {
  group: FulfillmentGroupData;
  parcel: OutboundParcel | undefined;
  parcelBeingEditedIndex: number;
}): number {
  const exactParcelIndex = parcel
    ? (group.selectedParcels?.indexOf(parcel) ?? -1)
    : -1;
  if (exactParcelIndex >= 0) {
    return exactParcelIndex;
  }

  if (group.selectedParcels?.[parcelBeingEditedIndex]) {
    return parcelBeingEditedIndex;
  }

  return 0;
}

function getParcelInsuranceUiSelection({
  coverageType,
  insuredAmount,
  packageCoverageLimit,
  parcelInsuranceThreshold,
}: {
  coverageType: ParcelInsuranceCoverageType | null | undefined;
  insuredAmount: number | null | undefined;
  packageCoverageLimit: number;
  parcelInsuranceThreshold: number;
}): ParcelInsuranceCoverageType {
  if (coverageType != null) {
    return coverageType;
  }

  if (insuredAmount == null || insuredAmount < parcelInsuranceThreshold) {
    return "included";
  }
  if (insuredAmount === packageCoverageLimit) {
    return "full";
  }
  return "custom";
}

export function useInsuranceInfo({
  parcelBeingEditedPerGroup,
  parcelBeingEditedIndex = 0,
  groups,
  team,
}: {
  parcelBeingEditedPerGroup: readonly OutboundParcel[];
  parcelBeingEditedIndex?: number;
  groups: readonly FulfillmentGroupData[];
  team: Team;
}): InsuranceInfo {
  if (groups.length === 0 || parcelBeingEditedPerGroup.length === 0) {
    return {
      canEdit: false,
      reason: NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED,
    };
  }

  // All groups must be eligible for insurance
  for (const group of groups) {
    const eligibility = fulfillmentGroupInsuranceEligible({
      team,
      fulfillmentGroup: group,
    });
    if (!eligibility.insuranceEligible) {
      return { canEdit: false, reason: eligibility.reason };
    }
  }

  const insuranceRatePercentage =
    team?.settings?.outboundLabels?.insuranceRatePercentage || 1;
  const teamInsuranceIncludedThreshold =
    team?.settings?.outboundLabels?.insuranceIncludedThreshold ??
    DEFAULT_INSURANCE_INCLUDED_THRESHOLD;

  // Compute the threshold for each group/parcel pair and verify they all match
  const thresholdResults = groups.map((group, i) => {
    const parcel = parcelBeingEditedPerGroup[i];
    return getInsuranceAmountIncludedByDefaultThreshold({
      carrier: group.selectedShippingRate?.carrier ?? null,
      service: group.selectedShippingRate?.service ?? null,
      teamInsuranceIncludedThreshold,
      packageWeight: parcel?.parcelAndContentWeight ?? null,
    });
  });

  const firstResult = thresholdResults[0];
  if (!firstResult) {
    return { canEdit: false, reason: NO_INSURANCE_REASON.NO_PARCELS_SELECTED };
  }

  const allThresholdsSame = thresholdResults.every(
    (r) => r.parcelInsuranceThreshold === firstResult.parcelInsuranceThreshold,
  );
  if (!allThresholdsSame) {
    return {
      canEdit: false,
      reason: NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED,
    };
  }

  const parcelInsuranceThreshold = firstResult.parcelInsuranceThreshold;
  const reason = firstResult.reason;

  // Aggregate costs across all groups and determine UI selection
  let totalFullCoverageInsuranceCost = 0;
  let totalCustomInsuranceDisplayedCost = 0;
  let allGroupsCanBeFullyInsured = true;
  let allDefault = true;
  let allFullCoverage = true;
  let mixedCustomInsurance = false;
  let firstCustomInsuredAmount: number | null | undefined;
  let firstInsuranceUiSelection: ParcelInsuranceCoverageType | undefined;
  let firstCustomParcel: OutboundParcel | undefined;
  let maxCoverageAmount = Infinity;
  let coverageExceedsMaximum = false;

  for (const [i, group] of groups.entries()) {
    const parcel = parcelBeingEditedPerGroup[i];
    const parcelIndex = getParcelIndexForInsurance({
      group,
      parcel,
      parcelBeingEditedIndex,
    });
    const parcelMaxInsuredValues = getFulfillmentGroupParcelMaxInsuredValues({
      group,
    });
    const parcelMaxInsuredValue = parcelMaxInsuredValues[parcelIndex];
    if (!parcelMaxInsuredValue) {
      return {
        canEdit: false,
        reason: NO_INSURANCE_REASON.NO_PARCELS_SELECTED,
      };
    }
    const packageCoverageLimit = parcelMaxInsuredValue.maxInsuredValue;
    maxCoverageAmount = Math.min(maxCoverageAmount, packageCoverageLimit);

    totalFullCoverageInsuranceCost += getParcelInsuranceCost({
      teamRatePercentage: insuranceRatePercentage,
      valueToInsure: packageCoverageLimit,
      parcelInsuranceThreshold,
    });

    const parcelInsuredAmount = parcel?.insurance?.insuredAmount;
    const parcelCoverageType = parcel?.insurance?.coverageType;
    const parcelInsuranceUiSelection = getParcelInsuranceUiSelection({
      coverageType: parcelCoverageType,
      insuredAmount: parcelInsuredAmount,
      packageCoverageLimit,
      parcelInsuranceThreshold,
    });
    if (
      parcelInsuranceUiSelection === "custom" &&
      firstCustomParcel === undefined
    ) {
      firstCustomParcel = parcel;
    }
    if (
      parcelInsuranceUiSelection === "custom" &&
      parcelInsuredAmount != null &&
      parcelInsuredAmount > packageCoverageLimit
    ) {
      coverageExceedsMaximum = true;
    }
    const cappedParcelInsuredAmount =
      parcelInsuredAmount != null
        ? Math.min(parcelInsuredAmount, packageCoverageLimit)
        : null;
    if (parcelInsuredAmount != null) {
      totalCustomInsuranceDisplayedCost += getParcelInsuranceCost({
        teamRatePercentage: insuranceRatePercentage,
        valueToInsure: cappedParcelInsuredAmount ?? 0,
        parcelInsuranceThreshold,
      });
    }
    if (packageCoverageLimit < parcelInsuranceThreshold) {
      allGroupsCanBeFullyInsured = false;
    }
    if (parcelInsuranceUiSelection !== "included") {
      allDefault = false;
    }

    if (parcelInsuranceUiSelection !== "full") {
      allFullCoverage = false;
    }

    const rawParcelInsuredAmount = parcelInsuredAmount ?? null;
    if (firstInsuranceUiSelection === undefined) {
      firstInsuranceUiSelection = parcelInsuranceUiSelection;
      firstCustomInsuredAmount = rawParcelInsuredAmount;
    } else if (
      parcelInsuranceUiSelection !== firstInsuranceUiSelection ||
      (parcelInsuranceUiSelection === "custom" &&
        rawParcelInsuredAmount !== firstCustomInsuredAmount)
    ) {
      mixedCustomInsurance = true;
    }
  }

  const representativeParcel =
    firstCustomParcel ?? parcelBeingEditedPerGroup[0];
  const insuredAmount = representativeParcel?.insurance?.insuredAmount ?? null;
  const coverageType = representativeParcel?.insurance?.coverageType ?? null;
  const includedCoverageAmount = Math.min(
    parcelInsuranceThreshold,
    maxCoverageAmount,
  );

  let insuranceUiSelection: ParcelInsuranceCoverageType;
  if (allDefault) {
    insuranceUiSelection = "included";
  } else if (allFullCoverage) {
    insuranceUiSelection = "full";
  } else if (firstCustomParcel) {
    insuranceUiSelection = "custom";
  } else {
    insuranceUiSelection = "included";
  }

  return {
    canEdit: true,
    isBulkEdit: groups.length > 1,
    mixedCustomInsurance,
    insuredAmount,
    totalFullCoverageInsuranceCost,
    insuranceUiSelection,
    coverageType,
    coverageExceedsMaximum,
    insuranceIncludedThreshold: parcelInsuranceThreshold,
    includedCoverageAmount,
    maxCoverageAmount,
    groupCanBeFullyInsured: allGroupsCanBeFullyInsured,
    insuranceCoverageReason: reason,
    customInsuranceDisplayedCost: totalCustomInsuranceDisplayedCost,
  };
}
